import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Icon from "./Icon";

const PAGE_TITLES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/map": "Map",
  "/leases": "Leases",
  "/analysis": "Analysis",
  "/sampling": "Sampling",
  "/bedding": "Bedding claims",
  "/verification": "Verification",
  "/submittals": "Submittals",
  "/annual-monitoring": "Annual monitoring",
  "/mortality-report": "Mortality reporting",
  "/bedding-request": "Bedding reimbursement",
  "/entities": "Entities",
  "/users": "User management",
  "/timekeeping": "Time & expenses",
  "/settings": "Profile & settings",
  "/credits": "Carbon credits",
  "/buy": "Buy credits",
  "/orders": "Orders",
  "/portfolio": "My credits",
  "/evidence": "Quality & evidence",
  "/file-share": "File share",
};

export default function Header({ onOpenNavigation }: { onOpenNavigation: () => void }) {
  const { profile, role, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = PAGE_TITLES[location.pathname]
    ?? (location.pathname.startsWith("/orders/")
      ? "Order detail"
      : location.pathname.startsWith("/retirements/")
        ? "Retirement certificate"
        : "");

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim();
  const displayName = fullName || profile?.email || "Account";
  const initials = fullName
    ? fullName.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()
    : profile?.email?.slice(0, 2).toUpperCase() ?? "CV";

  return (
    <header className="app-header relative z-30 flex h-[4.5rem] shrink-0 items-center justify-between px-4 sm:px-6 xl:px-8">
      <div className="flex min-w-0 items-center gap-3 md:hidden">
        <button type="button" onClick={onOpenNavigation} className="icon-button inline-flex" aria-label="Open navigation">
          <Icon name="menu" />
        </button>
        {pageTitle && <div className="truncate text-sm font-semibold text-[color:var(--color-text)]">{pageTitle}</div>}
      </div>

      <details className="account-menu group relative ml-auto">
        <summary className="flex cursor-pointer list-none items-center gap-3 rounded-lg p-1.5 pr-2 transition-colors hover:bg-[color:var(--color-surface-2)] [&::-webkit-details-marker]:hidden">
          <span className="avatar-mark">{initials}</span>
          <span className="hidden text-left sm:block">
            <span className="block max-w-44 truncate text-sm font-medium leading-tight">{displayName}</span>
            {role && <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-text-muted)]">{role}</span>}
          </span>
          <Icon name="chevron" className="hidden h-3.5 w-3.5 rotate-90 text-[color:var(--color-text-muted)] transition-transform group-open:rotate-[-90deg] sm:block" />
        </summary>
        <div className="absolute right-0 top-[calc(100%+0.5rem)] w-64 overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-surface)] shadow-[var(--shadow-deep)]">
          <div className="border-b border-[color:var(--color-rule)] px-4 py-3">
            <div className="truncate text-sm font-semibold">{displayName}</div>
            {profile?.email && fullName && <div className="mt-0.5 truncate text-xs text-[color:var(--color-text-muted)]">{profile.email}</div>}
          </div>
          <div className="p-1.5">
            <Link to="/settings" className="account-menu-item">
              <Icon name="settings" className="h-4 w-4" />
              Profile &amp; settings
            </Link>
            <button type="button" className="account-menu-item w-full"
              onClick={async () => {
                await signOut();
                navigate("/login", { replace: true });
              }}>
              <Icon name="logout" className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </details>
    </header>
  );
}
